import { useQuery } from "@tanstack/react-query";
import { PageBreadcrumbs } from "@/components/shared/page-breadcrumbs";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { apiGet } from "@/lib/api-client";
import { configKeys } from "@/lib/query";
import { configUserArraySchema } from "@/schemas/config";

const SKELETON_ROWS = ["sk-0", "sk-1", "sk-2", "sk-3", "sk-4", "sk-5", "sk-6", "sk-7"] as const;

export function ClaimDepartmentListPage() {
  // Filter by AD department (exact match, case-insensitive) instead of a free-
  // text search — `?search=Claims` would also match users whose name or title
  // contains "Claims". Reuses the existing query-key helper with a sentinel
  // search value so the cache key still differs from the unfiltered Users page.
  const usersQuery = useQuery({
    queryKey: configKeys.users({ search: "department:Claims" }),
    queryFn: () => apiGet("/api/config/users?department=Claims", configUserArraySchema),
  });

  return (
    <div className="space-y-6">
      <PageBreadcrumbs current="Claim Department List" />
      <div className="rounded-md border bg-card p-4">
        <h1 className="font-display text-3xl font-bold">Claim Department List</h1>
        <p className="mt-1 text-muted-foreground">All employees in the claims department.</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <h3 className="font-semibold">Claims Department Users</h3>
        </CardHeader>
        <CardContent>
          {usersQuery.isLoading && (
            <div className="space-y-2">
              {SKELETON_ROWS.map((key) => (
                <div key={key} className="h-8 animate-pulse rounded bg-muted" />
              ))}
            </div>
          )}

          {usersQuery.isError && (
            <p className="text-sm text-destructive">
              Failed to load users.{" "}
              <button type="button" className="underline" onClick={() => usersQuery.refetch()}>
                Retry
              </button>
            </p>
          )}

          {usersQuery.data && usersQuery.data.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No users found in the Claims department.
            </p>
          )}

          {usersQuery.data && usersQuery.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 font-medium">Account</th>
                    <th className="pb-2 font-medium">Name</th>
                    <th className="pb-2 font-medium">Title</th>
                    <th className="pb-2 font-medium">Department</th>
                    <th className="pb-2 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {usersQuery.data.map((user) => (
                    <tr key={user.id} className="border-b last:border-0">
                      <td className="py-2 font-mono text-xs">{user.samAccountName}</td>
                      <td className="py-2 font-medium">{user.name}</td>
                      <td className="py-2">{user.title ?? "—"}</td>
                      <td className="py-2">{user.department ?? "—"}</td>
                      <td className="py-2">
                        {user.email ? (
                          <a href={`mailto:${user.email}`} className="text-primary hover:underline">
                            {user.email}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
